import { Selectors } from "../../Everou/_reducers/app.reducer";
import { PMSLocationsActions } from "../_actions/PMSLocationsActions";
import { TimeHelper } from "../../Everou/_helpers";
import { WaitSpinner } from "../../Common/_components/WaitSpinner";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { CreateReservationInput } from "./_components/CreateReservationInput";
import { ReservationInput } from "./_components/ReservationInput";
import {
    ReservationsFilterBar,
    RESERVATION_STATUS_ALL,
    buildReservationFilterParams,
} from "../_components/ReservationsFilterBar";
import { VACATION_RENTAL_PLANS } from "../_constants/vacationRentalPlan.constants";
import { appPermissions } from "../../Everou/_constants/permissions.constants";

export function PMSLocationReservations() {

    // LIBRARIES
    const {t} = useTranslation();
    const dispatch = useDispatch();

    // GLOBAL STATE
    let reservations = [];
    reservations = useSelector(Selectors.getPMSLocationReservations);
    const locationUid = useSelector(Selectors.getPMSLocationSelectedUid);
    const timezone = useSelector(Selectors.getPMSLocationSelectedTimezone);
    // Reservation filtering is a PREMIUM-only feature. The plan comes from the
    // SELECTED list item, already in the store when this view mounts — reading
    // it from locationInfo (fetched after entry) made the first fetch go out
    // unfiltered and a second, filtered one fire once the info landed.
    const vacationRentalPlan = useSelector(
        Selectors.getPMSLocationSelectedVacationRentalPlan,
    );
    const canFilter = vacationRentalPlan === VACATION_RENTAL_PLANS.PREMIUM_PLAN;
    // Installers only see the reservations: creating (and editing, see
    // ReservationInput) is for the owner and the admins, as in the backend.
    const canManage = appPermissions.canManageReservations(
        useSelector(Selectors.getPMSLocationSelectedPermissionType),
    );

    // LOCAL STATE
    const [statusFilter, setStatusFilter] = useState(RESERVATION_STATUS_ALL);
    // Default the start-date filter to the current instant, matching the global
    // reservations view: past reservations are rarely interesting, and "clear
    // dates" still reveals them. The default is sent verbatim ("from now");
    // once the user picks a day (dateFromIsDefault → false) it widens to
    // start-of-day like any other pick.
    const [dateFrom, setDateFrom] = useState(() => TimeHelper.getTodayIsoString());
    const [dateFromIsDefault, setDateFromIsDefault] = useState(true);
    const [dateTo, setDateTo] = useState(null);
    // True while the fetch triggered below is in flight. Without it the view
    // renders whatever the store holds (the previous location's reservations,
    // or an unfiltered load) until the filtered response lands, so the list
    // visibly "jumps" once the filter applies.
    const [loading, setLoading] = useState(true);

    // Reset every filter back to its default when switching locations, so each
    // location opens on the "from today" view instead of inheriting the filters
    // (status, picked dates) left over from the previously selected one. Done as
    // a render-phase adjustment rather than an effect so the derived params and
    // the fetch below already see the fresh values — a single request, no flash
    // of the old location's reservations under the previous filters.
    const [prevLocationUid, setPrevLocationUid] = useState(locationUid);
    if (locationUid !== prevLocationUid) {
        setPrevLocationUid(locationUid);
        setStatusFilter(RESERVATION_STATUS_ALL);
        setDateFrom(TimeHelper.getTodayIsoString());
        setDateFromIsDefault(true);
        setDateTo(null);
        setLoading(true);
    }

    // Server-side filter params derived from the local filter state. Filtering
    // is PREMIUM-only, so non-premium locations always fetch unfiltered (and
    // ignore any filter state left over from another location).
    const { status: statusParam, date_from: dateFromParam, date_to: dateToParam } =
        canFilter
            ? buildReservationFilterParams({ statusFilter, dateFrom, dateTo, timezone, dateFromIsDefault })
            : {};

    // INITIALIZATION — refetch whenever the location or any filter changes.
    useEffect(() => {
        let cancelled = false;
        setLoading(true);
        dispatch(PMSLocationsActions.getReservations({
            status: statusParam,
            date_from: dateFromParam,
            date_to: dateToParam,
        })).finally(() => {
            if (!cancelled) setLoading(false);
        });
        return () => {
            cancelled = true;
        };
    }, [dispatch, locationUid, statusParam, dateFromParam, dateToParam]);

    return (
        <div className="py-4 c-grid">
            {content()}
        </div>
    )

    function content() {
        const hasActiveFilters =
            canFilter &&
            (statusFilter !== RESERVATION_STATUS_ALL || !!dateFrom || !!dateTo);
        const isEmpty = !reservations || reservations.length === 0;

        // No reservations and no filters applied → first-run empty state.
        if (isEmpty && !hasActiveFilters && !loading) {
            return (
                <div>
                    <span className="
                        w-100
                        pt-2
                        text-center
                    ">
                        {t('mod_integrations_reservations_not_yet')}
                    </span>
                    {canManage && <CreateReservationInput />}
                </div>
            )
        }

        return (
            <div>
                {canManage &&
                    <div className="mb-3">
                        <CreateReservationInput />
                    </div>
                }
                {canFilter && filterBar()}
                {loading
                    ? <div className="d-flex justify-content-center pt-4">
                        <WaitSpinner />
                    </div>
                    : isEmpty
                    ? <div className="pt-3 text-center text-secondary">
                        {t('mod_pms_reservation_filter_no_results')}
                    </div>
                    : reservations.map(reservation =>
                        <ReservationInput
                            key={reservation.uid}
                            reservation={reservation} />
                    )
                }
            </div>
        );
    }

    function filterBar() {
        return (
            <ReservationsFilterBar
                statusFilter={statusFilter}
                onStatusChange={setStatusFilter}
                dateFrom={dateFrom}
                dateTo={dateTo}
                onDateFromChange={onDateFromChange}
                onDateToChange={setDateTo}
                onClearDates={clearDates}
                timezone={timezone}
            />
        );
    }

    function onDateFromChange(value) {
        setDateFrom(value);
        setDateFromIsDefault(false);
    }

    function clearDates() {
        setDateFrom(null);
        setDateFromIsDefault(false);
        setDateTo(null);
    }

}
